<template>
    <div>
        <div v-if="loading" class="flex items-center justify-center py-24">
            <UIcon name="i-ph-spinner" class="animate-spin text-2xl text-gray-400" />
        </div>

        <div v-else-if="error" class="rounded-xl bg-white p-10 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-warning-circle" class="mb-3 text-4xl text-red-500" />
            <p class="text-lg text-gray-700">{{ error }}</p>
        </div>

        <div v-else-if="summary" class="space-y-6 print:space-y-4">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-center gap-3">
                    <img class="size-10" src="@/assets/img/logo.png" alt="SAMU Liban" />
                    <div>
                        <h2 class="text-lg font-semibold text-gray-800">Field Post Medical Summary</h2>
                        <p class="text-sm text-gray-500">Intervention #{{ String(summary.number).padStart(6, '0') }} · {{ summary.post }}</p>
                    </div>
                </div>
                <UButton color="white" icon="i-ph-printer-duotone" label="Print" class="print:hidden" @click="print()" />
            </div>

            <div class="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div class="border-b border-gray-300 px-6 py-3">
                    <h3 class="text-lg font-bold text-gray-700">Patient</h3>
                </div>
                <div class="grid grid-cols-2 gap-4 px-6 py-5 sm:grid-cols-4">
                    <UFormGroup label="Name" class="col-span-2">{{ patientName || '–' }}</UFormGroup>
                    <UFormGroup label="Date of Birth">
                        <template v-if="summary.patient.birth_date">
                            {{ dayjs(summary.patient.birth_date).format('MMM D, YYYY') }}
                            <span v-if="age !== null" class="text-gray-500">({{ age }})</span>
                        </template>
                        <template v-else>–</template>
                    </UFormGroup>
                    <UFormGroup label="Gender"><span class="capitalize">{{ summary.patient.gender || '–' }}</span></UFormGroup>
                    <UFormGroup label="Blood Type">{{ summary.patient.blood_type || '–' }}</UFormGroup>
                    <UFormGroup label="Allergies" class="col-span-2 sm:col-span-3">
                        <span :class="summary.allergies && summary.allergies !== 'None known' ? 'font-semibold text-red-600' : ''">{{ summary.allergies || 'Unknown' }}</span>
                    </UFormGroup>
                </div>
            </div>

            <div class="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div class="border-b border-gray-300 px-6 py-3">
                    <h3 class="text-lg font-bold text-gray-700">Intervention</h3>
                </div>
                <div class="space-y-4 px-6 py-5">
                    <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <UFormGroup label="Arrived">{{ formatTime(summary.arrived_at) }}</UFormGroup>
                        <UFormGroup label="Discharged">{{ formatTime(summary.discharged_at) }}</UFormGroup>
                        <UFormGroup label="Reason">{{ summary.reason || '–' }}</UFormGroup>
                        <UFormGroup label="Disposition">{{ summary.disposition || '–' }}</UFormGroup>
                    </div>
                    <UFormGroup v-if="summary.circumstances" label="Circumstances"><p class="whitespace-pre-line">{{ summary.circumstances }}</p></UFormGroup>
                    <UFormGroup v-if="summary.clinical_exam" label="Clinical Examination"><p class="whitespace-pre-line">{{ summary.clinical_exam }}</p></UFormGroup>
                    <UFormGroup v-if="summary.precautions" label="Special Notes and Precautions"><p class="whitespace-pre-line">{{ summary.precautions }}</p></UFormGroup>
                    <UFormGroup v-if="summary.progress_notes" label="Progress Notes"><p class="whitespace-pre-line">{{ summary.progress_notes }}</p></UFormGroup>
                </div>
            </div>

            <div class="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div class="border-b border-gray-300 px-6 py-3">
                    <h3 class="text-lg font-bold text-gray-700">Vitals</h3>
                </div>
                <table class="w-full text-sm">
                    <thead>
                        <tr class="text-left text-gray-500">
                            <th class="px-6 py-2 font-medium" />
                            <th class="px-6 py-2 font-medium">Initial</th>
                            <th class="px-6 py-2 font-medium">Post-Treatment</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                        <tr v-for="row in vitalRows" :key="row.label">
                            <td class="px-6 py-2 text-gray-500">{{ row.label }}</td>
                            <td class="px-6 py-2 font-mono text-gray-800">{{ row.initial }}</td>
                            <td class="px-6 py-2 font-mono text-gray-800">{{ row.post }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                <div class="border-b border-gray-300 px-6 py-3">
                    <h3 class="text-lg font-bold text-gray-700">Treatment Given</h3>
                </div>
                <div v-if="!summary.treatments.length && !summary.medications.length" class="px-6 py-5 text-sm italic text-gray-500">No treatments or medications recorded.</div>
                <ul v-else class="divide-y divide-dashed divide-gray-300">
                    <li v-for="(t, index) in summary.treatments" :key="`t${index}`" class="flex items-center justify-between gap-4 px-6 py-3">
                        <span class="text-gray-800">{{ t.label }}</span>
                        <span class="text-sm text-gray-500">{{ formatTime(t.given_at) }}</span>
                    </li>
                    <li v-for="(m, index) in summary.medications" :key="`m${index}`" class="flex items-center justify-between gap-4 px-6 py-3">
                        <div>
                            <p class="font-medium text-gray-800">{{ m.name }}</p>
                            <p class="text-sm text-gray-500">{{ [m.dose, m.route?.toUpperCase(), `${m.qty} ${m.unit ?? ''}`.trim()].filter(Boolean).join(' · ') }}</p>
                        </div>
                        <span class="text-sm text-gray-500">{{ formatTime(m.given_at) }}</span>
                    </li>
                </ul>
            </div>

            <p class="text-center text-xs text-gray-500">
                Seen by {{ [summary.physician, summary.nurse].filter(Boolean).join(' and ') || 'the SAMU Liban field team' }}.
                This summary was prepared by SAMU Liban for hospital admission.
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { VITAL_FIELDS, type Vitals } from '~/types/fieldPost'
import { ageFromBirthDate } from '~/utils/fieldPosts'

definePageMeta({ layout: 'transport' })
useHead({ title: 'Medical Summary' })

type Summary = {
    number: number
    post: string | null
    arrived_at: string
    discharged_at: string | null
    patient: { first_name: string | null; last_name: string | null; birth_date: string | null; gender: string | null; blood_type: string | null }
    allergies: string | null
    reason: string | null
    circumstances: string | null
    vitals_initial: Vitals
    vitals_post: Vitals
    clinical_exam: string | null
    precautions: string | null
    progress_notes: string | null
    disposition: string | null
    physician: string | null
    nurse: string | null
    treatments: { label: string | null; given_at: string | null }[]
    medications: { name: string; dose: string | null; route: string | null; qty: number; unit: string | null; given_at: string | null }[]
}

const route = useRoute()
const loading = ref(true)
const error = ref('')
const summary = ref<Summary>()

const patientName = computed(() => [summary.value?.patient.first_name, summary.value?.patient.last_name].filter(Boolean).join(' '))
const age = computed(() => ageFromBirthDate(summary.value?.patient.birth_date ?? null, new Date()))

const vitalRows = computed(() => {
    const initial = summary.value?.vitals_initial ?? {}
    const post = summary.value?.vitals_post ?? {}
    const format = (v: Vitals, key: keyof Vitals, unit: string) => v[key] === undefined ? '–' : `${v[key]} ${unit}`
    const bp = (v: Vitals) => v.bp_systolic === undefined && v.bp_diastolic === undefined ? '–' : `${v.bp_systolic ?? '–'}/${v.bp_diastolic ?? '–'} mmHg`
    const rows = [{ label: 'BP', initial: bp(initial), post: bp(post) }]
    for (const field of VITAL_FIELDS) {
        if (field.key === 'bp_systolic' || field.key === 'bp_diastolic') continue
        const unit = /^\d/.test(field.unit) ? '' : field.unit
        rows.push({ label: field.label, initial: format(initial, field.key, unit), post: format(post, field.key, unit) })
    }
    return rows.filter(r => r.initial !== '–' || r.post !== '–')
})

function formatTime(iso: string | null) {
    return iso ? dayjs(iso).format('MMM D, YYYY HH:mm') : '–'
}

function print() {
    window.print()
}

onMounted(async () => {
    const token = route.query.token as string
    if (!token) {
        error.value = 'This link is invalid.'
        loading.value = false
        return
    }
    try {
        summary.value = await $fetch<Summary>(`/api/public/field-post-summary/${encodeURIComponent(token)}`)
    } catch (e) {
        error.value = (e as { data?: { message?: string } }).data?.message ?? 'This link is invalid or has expired.'
    }
    loading.value = false
})
</script>
